import React, { useMemo } from "react";

// Warm restaurant palette — no gradients, solid slices
const PALETTE = [
  "#7A0F14", // deep burgundy
  "#D4AF37", // royal gold
  "#B45309", // burnt orange
  "#4B7F3D", // olive
  "#8B5A2B", // sienna
  "#1F5460", // teal
  "#8E4162", // wine plum
  "#C0724E", // terracotta
  "#5D5FEF", // indigo
  "#7A6C5D", // taupe
];

function polar(cx, cy, r, angleDeg) {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
}

function arc(cx, cy, rOuter, rInner, startAngle, endAngle) {
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  const [x1, y1] = polar(cx, cy, rOuter, startAngle);
  const [x2, y2] = polar(cx, cy, rOuter, endAngle);
  const [x3, y3] = polar(cx, cy, rInner, endAngle);
  const [x4, y4] = polar(cx, cy, rInner, startAngle);
  return [
    `M ${x1} ${y1}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${x2} ${y2}`,
    `L ${x3} ${y3}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 0 ${x4} ${y4}`,
    "Z",
  ].join(" ");
}

/**
 * Donut chart for spend-by-category.
 * `data`: [{ category, purchase, expense, count }]
 */
export default function CategoryDonut({ data = [], size = 220, testId }) {
  const rows = useMemo(() => {
    const withTotal = (data || [])
      .map((d) => ({
        category: d.category,
        total: Number(d.purchase || 0) + Number(d.expense || 0),
      }))
      .filter((d) => d.total > 0)
      .sort((a, b) => b.total - a.total);
    const grand = withTotal.reduce((s, r) => s + r.total, 0);
    return { rows: withTotal, grand };
  }, [data]);

  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size / 2 - 4;
  const rInner = rOuter - 32;

  if (!rows.rows.length) {
    return (
      <div
        className="flex items-center justify-center text-muted-foreground text-sm"
        style={{ width: size, height: size }}
        data-testid={testId}
      >
        No spend yet
      </div>
    );
  }

  let angle = 0;
  const slices = rows.rows.map((row, i) => {
    const pct = row.total / rows.grand;
    const start = angle;
    const end = angle + pct * 360;
    angle = end;
    const color = PALETTE[i % PALETTE.length];
    // If exactly one slice (100%), draw a full ring instead of a wedge
    if (rows.rows.length === 1) {
      return {
        d:
          `M ${cx + rOuter} ${cy} A ${rOuter} ${rOuter} 0 1 1 ${cx - rOuter} ${cy} ` +
          `A ${rOuter} ${rOuter} 0 1 1 ${cx + rOuter} ${cy} Z ` +
          `M ${cx + rInner} ${cy} A ${rInner} ${rInner} 0 1 0 ${cx - rInner} ${cy} ` +
          `A ${rInner} ${rInner} 0 1 0 ${cx + rInner} ${cy} Z`,
        color,
        row,
        pct,
      };
    }
    return { d: arc(cx, cy, rOuter, rInner, start, end), color, row, pct };
  });

  return (
    <div className="flex flex-col sm:flex-row items-center gap-8" data-testid={testId}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {slices.map((s, i) => (
            <path key={i} d={s.d} fill={s.color} fillRule="evenodd">
              <title>
                {s.row.category}: ₹{s.row.total.toLocaleString("en-IN")} ({(s.pct * 100).toFixed(1)}%)
              </title>
            </path>
          ))}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-[10px] tracking-[0.25em] text-muted-foreground">TOTAL</span>
          <span className="font-serif text-2xl mt-1" data-testid="donut-total">
            ₹{rows.grand.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
          </span>
        </div>
      </div>

      <ul className="space-y-2 text-sm w-full sm:w-auto">
        {slices.map((s, i) => (
          <li key={i} className="flex items-center gap-3 min-w-[220px]">
            <span
              aria-hidden="true"
              className="inline-block w-3 h-3 rounded-sm shrink-0"
              style={{ backgroundColor: s.color }}
            />
            <span className="flex-1 truncate">{s.row.category}</span>
            <span className="font-serif tabular-nums">
              ₹{s.row.total.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
            </span>
            <span className="text-xs text-muted-foreground w-10 text-right tabular-nums">
              {(s.pct * 100).toFixed(0)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
